import { trackZipCodeProtectionViewBlockedPdpPageView } from "@faire/web-api/events/zipCodeProtection/view/viewBlockedPdpPage";
import { IProductPageResponse } from "@faire/web-api/indigofair/data/IProductPageResponse";
import { IRetailerRestrictionsRequest } from "@faire/web-api/indigofair/data/IRetailerRestrictionsRequest";
import { IRetailerRestrictionsResponse } from "@faire/web-api/indigofair/data/IRetailerRestrictionsResponse";
import { QueryParams } from "@faire/web/common/query-params/QueryParams";
import React, { useState, Suspense, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { LazyBrandRestrictionModal } from "@faire/retailer/components/Modals/BrandRestrictionModal";
import { LazyQuickAddModal } from "@faire/retailer/features/ProductQuickAddModal/LazyQuickAddModal";
import { getIsBrandPreview } from "@faire/retailer/lib/getIsBrandPreview";
import { useIsLoggedInRetailerOrBrandPreview } from "@faire/retailer/lib/isLoggedInRetailer";
import { UpdateCartHandler } from "@faire/retailer/stores/domain/CartStore/consts";
import { useFaireProtection } from "@faire/retailer/stores/domain/FaireProtection/useFaireProtection";
import { useProductCacheStore } from "@faire/retailer/stores/domain/ProductCacheStore";
import { useRetailerStore } from "@faire/retailer/stores/domain/RetailerStore";

const useHasFaireProtections = (
  retailerToken?: string,
  brandToken?: string
) => {
  const { isBrandTokenForbidden } = useFaireProtection();
  const hasFaireProtection = useMemo(() => {
    if (!retailerToken || !brandToken || getIsBrandPreview()) {
      return false;
    }

    return isBrandTokenForbidden(brandToken, { trackReason: true });
  }, [brandToken, isBrandTokenForbidden, retailerToken]);

  return hasFaireProtection;
};

export const ProductModals: React.FC<{
  productToken?: string;
  brand?: IProductPageResponse.IPdpBrand;
  brandToken?: string;
  queryParams?: QueryParams;
  updateCartFn?: UpdateCartHandler;
}> = ({ productToken, brand, brandToken, queryParams, updateCartFn }) => {
  const { retailerToken } = useRetailerStore(["retailerToken"]);
  const { goBack } = useHistory();
  const { getRetailerRestrictions } = useProductCacheStore([
    "getRetailerRestrictions",
  ]);
  const [restrictions, setRestrictions] = useState<
    IRetailerRestrictionsResponse | undefined
  >(undefined);
  const [closeFaireProtectionModal, setCloseFaireProtectionModal] =
    useState(false);

  useEffect(() => {
    if (retailerToken && brandToken && !getIsBrandPreview()) {
      getRetailerRestrictions(
        retailerToken ?? "",
        IRetailerRestrictionsRequest.build({
          brand_token: brandToken,
          product_token: productToken,
        })
      ).then((response) => setRestrictions(response));
    }
  }, [brandToken, getRetailerRestrictions, productToken, retailerToken]);

  const hasFaireProtections = useHasFaireProtections(retailerToken, brandToken);

  const onCloseFaireProtectionModal = () => {
    setCloseFaireProtectionModal(true);
    trackZipCodeProtectionViewBlockedPdpPageView(
      brandToken ?? "",
      productToken ?? ""
    );
  };

  const isLoggedInRetailerOrBrandPreview =
    useIsLoggedInRetailerOrBrandPreview();

  if (!brand) {
    return null;
  }
  if (
    restrictions !== undefined &&
    restrictions.retailer_restrictions?.length
  ) {
    return (
      <Suspense fallback={null}>
        <LazyBrandRestrictionModal
          brand={brand}
          origin="PRODUCT_PAGE"
          retailerRestriction={
            restrictions?.retailer_restrictions?.length
              ? restrictions?.retailer_restrictions[0]
              : undefined
          }
          productToken={productToken}
        />
      </Suspense>
    );
  } else if (hasFaireProtections && !closeFaireProtectionModal) {
    return (
      <Suspense fallback={null}>
        <LazyBrandRestrictionModal
          brand={brand}
          onClose={onCloseFaireProtectionModal}
        />
      </Suspense>
    );
  } else if (queryParams?.get("product") && isLoggedInRetailerOrBrandPreview) {
    return (
      <Suspense fallback={null}>
        <LazyQuickAddModal
          onClose={() => goBack()}
          productToken={queryParams?.get("product") ?? ""}
          updateCartFn={updateCartFn}
        />
      </Suspense>
    );
  } else {
    return null;
  }
};
